import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Box,
  IconButton,
  Button,
  Typography
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import { getAllFolders } from '../../api/folderAPI';
import { updateFileAccess } from '../../api/fileAPI';
import { useTranslation } from 'react-i18next';

interface FileEntry {
  fileId: string;
  fileName: string;
  folderName: string;
  view: boolean;
  download: boolean;
}

interface FolderData {
  folderId: string;
  folderName: string;
  files: {
    fileId: string;
    fileName: string;
    fileAccess?: {
      view: boolean;
      download: boolean;
    };
  }[];
  subfolders: FolderData[];
}

interface ModalFilesProps {
  open: boolean;
  onClose: () => void;
}

const ModalFiles: React.FC<ModalFilesProps> = ({ open, onClose }) => {
  const [flattenedData, setFlattenedData] = useState<FileEntry[]>([]);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const storedLang = localStorage.getItem('language') || 'EN';
    i18n.changeLanguage(storedLang);
  }, []);

  const flattenFolders = (folders: FolderData[], parentPath = ''): FileEntry[] => {
    let result: FileEntry[] = [];

    folders.forEach(folder => {
      const folderPath = parentPath ? `${parentPath} / ${folder.folderName}` : folder.folderName;

      folder.files.forEach(file => {
        result.push({
          fileId: file.fileId,
          fileName: file.fileName,
          folderName: folderPath,
          view: file.fileAccess?.view ?? false,
          download: file.fileAccess?.download ?? false,
        });
      });

      if (folder.subfolders && folder.subfolders.length > 0) {
        result = result.concat(flattenFolders(folder.subfolders, folderPath));
      }
    });

    return result;
  };

  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      try {
        const folders: FolderData[] = await getAllFolders();
        const flattened = flattenFolders(folders);
        setFlattenedData(flattened);
      } catch (err) {
        console.error('Error loading folders or files', err);
      }
    };

    fetchData();
  }, [open]);

  const handleCheckboxChange = (index: number, field: 'view' | 'download', value: boolean) => {
    setFlattenedData(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSave = async () => {
    for (const entry of flattenedData) {
      try {
        await updateFileAccess(entry.fileId, entry.view, entry.download);
      } catch (err) {
        console.error(`Error updating access for file ${entry.fileName}`, err);
      }
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <InsertDriveFileIcon sx={{ marginRight: 1, color: '#73AF73' }} />
          <Typography variant="h6" sx={{ color: '#73AF73' }}>{t('files')}</Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Table>
          <TableHead>
            <TableRow sx={{ borderBottom: '2px solid #ccc' }}>
              <TableCell><b>{t('folder')}</b></TableCell>
              <TableCell><b>{t('file')}</b></TableCell>
              <TableCell><b>{t('view')}</b></TableCell>
              <TableCell><b>{t('download')}</b></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {flattenedData.map((entry, index) => (
              <TableRow key={entry.fileId}>
                <TableCell>{entry.folderName}</TableCell>
                <TableCell>{entry.fileName}</TableCell>
                <TableCell>
                  <Checkbox
                    checked={entry.view}
                    onChange={(e) => handleCheckboxChange(index, 'view', e.target.checked)}
                  />
                </TableCell>
                <TableCell>
                  <Checkbox
                    checked={entry.download}
                    onChange={(e) => handleCheckboxChange(index, 'download', e.target.checked)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button variant="contained" onClick={handleSave} sx={{ backgroundColor: '#73AF73' }}>
            {t('save')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ModalFiles;